(function () {
  'use strict';
  if (window.MWStorefrontAudio) return;
  const mounts = new WeakMap();
  function productGid(value) {
    const id = String(value || '');
    if (/^[1-9][0-9]*$/.test(id)) return `gid://shopify/Product/${id}`;
    if (/^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(id)) return id;
    return null;
  }
  async function publishedContent(mode) {
    if (!['simple', 'comparison'].includes(mode)) throw new TypeError('Unsupported audio presentation.');
    const content = window.MWStorefrontContent;
    if (!content) throw new Error('Published content is unavailable.');
    await content.ready;
    if (!content.available) throw new Error('Published content is unavailable.');
    return content;
  }
  async function select({ productId, mode }) {
    const content = await publishedContent(mode);
    const gid = productGid(productId);
    if (!gid) return { managed: false, tracks: [] };
    const selection = content.productAudio(gid);
    if (!selection.managed) return { managed: false, tracks: [] };
    if (selection.sets.some(set => set.mode !== mode)) throw new Error('Published audio does not match this player presentation.');
    return { managed: true, tracks: selection.sets.flatMap(set => set.tracks) };
  }
  async function selectFeature({ productId, audioSetId }) {
    const gid = productGid(productId);
    const setId = typeof audioSetId === 'string' ? audioSetId.trim() : '';
    if (!gid || !setId) return { managed: false, tracks: [] };
    const content = await publishedContent('comparison');
    const selection = content.productFeatureAudio(gid, setId);
    return { managed: selection.managed, tracks: selection.audio?.tracks || [] };
  }
  async function selectPublishedSet({ setAlias, mode }) {
    const content = await publishedContent(mode);
    const set = content.record('audioSet', setAlias);
    if (!set) return { managed: false, tracks: [] };
    if (set.mode !== mode) throw new Error('Published audio does not match this presentation.');
    return { managed: true, tracks: set.items.map(item => {
      const demo = content.record('demo', item.demoId);
      if (!demo || demo.mode !== mode || (mode === 'comparison' && !demo.media.video)) throw new Error('Published audio is incomplete.');
      return { title: item.title, artist: item.artist, ...(mode === 'comparison' ? { songTitle: item.title } : {}), ...demo.media };
    }) };
  }
  function mountPlayer(options, selection, player) {
    const root = options.root;
    if (!root) return Promise.resolve(null);
    if (mounts.has(root)) return mounts.get(root).pending;
    const lifecycle = { pending: null, instance: null, cancelled: false };
    mounts.set(root, lifecycle);
    const current = () => !lifecycle.cancelled && mounts.get(root) === lifecycle;
    root.setAttribute('data-mw-content-managed', '');
    root.hidden = true;
    lifecycle.pending = (async () => {
      try {
        const { tracks } = await selection(options);
        if (!current()) return null;
        if (!tracks.length) { root.hidden = true; return null; }
        root.hidden = false;
        const Player = player();
        if (typeof Player !== 'function') throw new Error('The audio player is unavailable.');
        const instance = new Player({ root, tracks });
        lifecycle.instance = instance;
        if (instance.ready) await instance.ready;
        if (!current()) return null;
        root.removeAttribute?.('data-mw-content-error');
        return instance;
      } catch (error) {
        if (!current()) return null;
        if (lifecycle.instance && typeof lifecycle.instance.destroy === 'function') lifecycle.instance.destroy();
        lifecycle.instance = null;
        root.hidden = true;
        root.setAttribute('data-mw-content-error', '');
        console.warn('[MixWave content] Audio could not be displayed.', error.message);
        return null;
      }
    })();
    return lifecycle.pending;
  }
  function unmount(root) {
    const lifecycle = root && mounts.get(root);
    if (!lifecycle) return;
    lifecycle.cancelled = true;
    mounts.delete(root);
    if (lifecycle.instance && typeof lifecycle.instance.destroy === 'function') lifecycle.instance.destroy();
    lifecycle.instance = null;
    root.hidden = true;
  }
  function mount(options) {
    return mountPlayer(options, select, () => options.mode === 'comparison' ? window.MWAudioComparisonPlayer : window.MWAudioPlayer);
  }
  function mountFeature(options) {
    return mountPlayer(options, selectFeature, () => window.MWFeatureAudioPlayer);
  }
  window.MWStorefrontAudio = Object.freeze({ select, selectFeature, selectPublishedSet, mount, mountFeature, unmount, productGid });
})();
